import React, { useState } from 'react';
import { Mail, MapPin, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { submitContactMessage } from '../api/client';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';

export const ContactSection = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitContactMessage(formData);
      toast.success(t('contact.successMessage'));
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('contact.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  };

  const primaryColor = settings.colors?.primary || '#0E7A5F';

  return (
    <section id="contact" className="bg-gray-50 py-20">
      <div className="container mx-auto px-6">
        <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">
          {getBilingualValue(settings.contact?.title, 'contact.title')}
        </h2>
        <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto mb-16">
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
            <MessageSquare size={48} style={{ color: primaryColor }} className="mb-6 mx-auto" />
            <h3 className="text-xl font-bold mb-2">{t('contact.whatsapp')}</h3>
            <p className="text-gray-600 mb-4">{settings.contact?.whatsapp || '+905522829983'}</p>
            <a
              href={`https://wa.me/${(settings.contact?.whatsapp || '+905522829983').replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block font-medium"
              style={{ color: primaryColor }}
            >
              {t('contact.sendMessage')} →
            </a>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
            <Mail size={48} style={{ color: primaryColor }} className="mb-6 mx-auto" />
            <h3 className="text-xl font-bold mb-2">{t('contact.email')}</h3>
            <p className="text-gray-600 mb-4">{settings.contact?.email || 'support@luxovera.com'}</p>
            <a
              href={`mailto:${settings.contact?.email || 'support@luxovera.com'}`}
              className="inline-block font-medium"
              style={{ color: primaryColor }}
            >
              {t('contact.sendEmail')} →
            </a>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
            <MapPin size={48} style={{ color: primaryColor }} className="mb-6 mx-auto" />
            <h3 className="text-xl font-bold mb-2">{t('contact.location')}</h3>
            <p className="text-gray-600 mb-4">{settings.contact?.location || 'Batam & Bintan, Indonesia'}</p>
            <button
              onClick={() => scrollToSection('destinations')}
              className="inline-block font-medium"
              style={{ color: primaryColor }}
            >
              {t('contact.viewDestinations')} →
            </button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto bg-white p-10 rounded-3xl shadow-xl">
          <h3 className="text-2xl font-bold mb-6">{t('contact.formTitle')}</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="contact-name">{t('contact.name')}</Label>
                <Input 
                  id="contact-name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  className="mt-2" 
                  required
                />
              </div>
              <div>
                <Label htmlFor="contact-email">{t('contact.email')}</Label>
                <Input 
                  id="contact-email" 
                  name="email" 
                  type="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  className="mt-2"
                  required 
                />
              </div>
            </div>
            <div>
              <Label htmlFor="contact-message">{t('form.message')}</Label>
              <Textarea 
                id="contact-message" 
                name="message" 
                rows={4} 
                value={formData.message} 
                onChange={handleChange} 
                className="mt-2"
                placeholder={t('contact.messagePlaceholder')}
                required
              />
            </div>
            <Button
              type="submit"
              className="text-white px-8"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = settings.colors?.primaryHover || '#0A5F4A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('contact.sending') : t('contact.sendButton')}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
